import React from 'react';
import { Table, Tag, Button } from 'antd';
import { useList } from '@refinedev/core';
import { RaceApplicationDTO } from '../../types/race/models';

interface ApplicationListProps {
	raceId: string;
	onShowDetail: (id: string) => void;
}

export const ApplicationList: React.FC<ApplicationListProps> = ({
	raceId,
	onShowDetail,
}) => {
	const { data, isLoading, isError } = useList<RaceApplicationDTO>({
		resource: "race_applications",
		filters: [
			{
				field: "race_master_id",
				operator: "eq",
				value: raceId,
			},
		],
		pagination: {
			pageSize: 50,
		},
		sorters: [
			{
				field: "created_at",
				order: "desc"
			}
		],
		queryOptions: {
			enabled: !!raceId,
			retry: 1,
			onError: (error) => {
				console.error('Error fetching applications:', error);
			},
		},
	});

	if (isError) {
		return (
			<div style={{ padding: '24px' }}>
				データの取得に失敗しました。再度お試しください。
			</div>
		);
	}

	console.log('Applications List Data:', { isLoading, data });

	const columns = [
		{
			title: '艦名',
			dataIndex: 'yacht_name',
			key: 'yacht_name',
			sorter: (a: RaceApplicationDTO, b: RaceApplicationDTO) =>
				(a.yacht_name || '').localeCompare(b.yacht_name || ''),
			width: '20%',
		},
		{
			title: 'オーナー',
			key: 'owner_name',
			render: (_: unknown, record: RaceApplicationDTO) => {
				return `${record.owner_last_name} ${record.owner_first_name}` || '-';
			},
			sorter: (a: RaceApplicationDTO, b: RaceApplicationDTO) => {
				const aFullName = `${a.owner_last_name} ${a.owner_first_name}`;
				const bFullName = `${b.owner_last_name} ${b.owner_first_name}`;
				return aFullName.localeCompare(bFullName);
			},
		},
		{
			title: 'セール番号',
			dataIndex: 'sail_number',
			key: 'sail_number',
		},
		{
			title: '艦種',
			dataIndex: 'yacht_type',
			key: 'yacht_type',
		},
		{
			title: 'ステータス',
			dataIndex: 'status',
			key: 'status',
			filters: [
				{ text: '申込済', value: 'completed' },
				{ text: '下書き', value: 'draft' },
				{ text: '取消済', value: 'cancelled' },
			],
			defaultFilteredValue: ['completed', 'cancelled'],
			onFilter: (value: any, record: RaceApplicationDTO) => record.status === value,
			render: (status: string) => (
				<Tag color={
					status === 'completed' ? 'success' :
						status === 'draft' ? 'processing' :
							status === 'cancelled' ? 'error' :
								'default'
				}>
					{status === 'draft' ? '下書き' :
						status === 'completed' ? '申込済' :
							status === 'cancelled' ? '取消済' :
								'その他'}
				</Tag>
			),
		},
		{
			title: '操作',
			key: 'action',
			render: (_: unknown, record: RaceApplicationDTO) => (
				<Button
					type="link"
					onClick={() => onShowDetail(record.id)}
				>
					詳細
				</Button>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={data?.data}
			rowKey="id"
			loading={isLoading}
		/>
	);
};
